/**
 * Google Drive Picker Adapter (Option A)
 * Requests the drive.readonly scope on demand when the user clicks "Select from Drive".
 * Uses the Google Picker API to let users select documents from their Drive.
 */

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY || '';
const SCOPES = 'https://www.googleapis.com/auth/drive.readonly';

let pickerApiLoaded = false;
// @ts-ignore
let gapiLoaded = false;
let gisLoaded = false;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

async function loadGapiPicker(): Promise<void> {
  if (pickerApiLoaded) return;
  await loadScript('https://apis.google.com/js/api.js');
  gapiLoaded = true;
  return new Promise((resolve, reject) => {
    (window as any).gapi.load('picker', {
      callback: () => { pickerApiLoaded = true; resolve(); },
      onerror: () => reject(new Error('Failed to load Picker API')),
    });
  });
}

async function loadGis(): Promise<void> {
  if (gisLoaded) return;
  await loadScript('https://accounts.google.com/gsi/client');
  gisLoaded = true;
}

/**
 * Request an OAuth2 access token with drive.readonly scope.
 * Uses Google Identity Services (GIS) for the token flow.
 */
function requestAccessToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!GOOGLE_CLIENT_ID) {
      reject(new Error('Google Client ID is not configured'));
      return;
    }

    const tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: SCOPES,
      callback: (response: any) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
          return;
        }
        resolve(response.access_token);
      },
      error_callback: (error: any) => {
        reject(new Error(error.message || 'Token request failed'));
      },
    });

    tokenClient.requestAccessToken({ prompt: 'consent' });
  });
}

export interface DrivePickResult {
  id: string;
  name: string;
  mimeType: string;
  url: string;
}

/**
 * Open Google Picker and let user select a document.
 * Returns file metadata or null if cancelled.
 */
function openPicker(accessToken: string): Promise<DrivePickResult | null> {
  return new Promise((resolve) => {
    const google = (window as any).google;
    const picker = new google.picker.PickerBuilder()
      .addView(new google.picker.DocsView()
        .setIncludeFolders(false)
        .setMimeTypes('text/plain,text/html,text/markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword'))
      .setOAuthToken(accessToken)
      .setDeveloperKey(GOOGLE_API_KEY || undefined)
      .setCallback((data: any) => {
        if (data.action === google.picker.Action.PICKED) {
          const doc = data.docs[0];
          resolve({
            id: doc.id,
            name: doc.name,
            mimeType: doc.mimeType,
            url: doc.url,
          });
        } else if (data.action === google.picker.Action.CANCEL) {
          resolve(null);
        }
      })
      .build();
    picker.setVisible(true);
  });
}

/**
 * Fetch file content from Google Drive.
 * Returns { text: string } for text files and Google Docs,
 * or { file: File } for binary files like PDF/DOCX.
 */
async function fetchDriveFileContent(fileId: string, mimeType: string, fileName: string, accessToken: string): Promise<{ text?: string; file?: File }> {
  let url: string;
  if (mimeType === 'application/vnd.google-apps.document') {
    url = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=text/plain`;
  } else {
    url = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
  }

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch file from Drive: ${response.status} ${response.statusText}`);
  }

  if (mimeType === 'application/pdf' || mimeType.includes('wordprocessingml') || mimeType === 'application/msword') {
    const blob = await response.blob();
    const file = new File([blob], fileName, { type: mimeType });
    return { file };
  }

  return { text: await response.text() };
}

/**
 * Full flow: load APIs, get token, open picker, fetch content.
 * Returns the file text/file + metadata, or null if cancelled.
 */
export async function pickFileFromDrive(): Promise<{ text?: string; file?: File; name: string; mime: string } | null> {
  await Promise.all([loadGapiPicker(), loadGis()]);
  const accessToken = await requestAccessToken();
  const picked = await openPicker(accessToken);
  if (!picked) return null;

  const content = await fetchDriveFileContent(picked.id, picked.mimeType, picked.name, accessToken);
  return { ...content, name: picked.name, mime: picked.mimeType };
}
